import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { apiService } from '../services/apiService'

export interface User {
  id: number
  email: string
  first_name?: string
  last_name?: string
  weight: number
  age: number
  height: number
  gender: string
  body_fat_percentage?: number
  liver_function: string
  kidney_function: string
  email_notifications_enabled: boolean
  // Granular email notification settings
  email_overuse_alerts: boolean
  email_reminders: boolean
  email_low_stash_alerts: boolean
  email_morning_reflections: boolean
  timezone: string
  has_updated_profile: boolean
  is_admin?: boolean
  terms_accepted?: boolean
  // Accessibility settings
  reduce_motion: boolean
}

interface LoginCredentials {
  email: string
  password: string
}

export interface RegisterData extends LoginCredentials {
  first_name?: string
  last_name?: string
  weight?: number
  age?: number
  height?: number // Made optional since field is hidden
  gender?: string
  body_fat_percentage?: number // Field is hidden
  liver_function?: string
  kidney_function?: string
  email_notifications_enabled: boolean
  // Granular email notification settings
  email_overuse_alerts: boolean
  email_reminders: boolean
  email_low_stash_alerts: boolean
  email_morning_reflections: boolean
  timezone: string
  conditions?: string[]
  // Accessibility settings
  reduce_motion?: boolean
}

export const useAuthStore = defineStore('auth', () => {
  const token = ref<string | null>(localStorage.getItem('token'))
  const user = ref<User | null>(null)
  const isAuthenticated = ref<boolean>(!!token.value)
  const refreshTimeout = ref<number | null>(null)

  // Initialize user data if token exists
  if (token.value) {
    refreshToken().then((success) => {
      if (success) {
        fetchUserData().then(() => {
          if (user.value) {
            scheduleTokenRefresh()
          }
        })
      } else if (!token.value) {
        // refreshToken() cleared the token because it was invalid
        logout()
      } else {
        // Likely a network or server error - keep existing token
        console.warn('Token refresh failed, retaining existing token')
      }
    })
  }

  // Note: Authentication and token refresh are now handled by apiService

  async function refreshToken(): Promise<boolean> {
    try {
      if (!token.value) {
        console.error('No token available for refresh');
        return false;
      }

      const data = await apiService.post('/token/refresh', null);
      
      const newToken = data.access_token;
      if (newToken) {
        token.value = newToken;
        localStorage.setItem('token', newToken);
        isAuthenticated.value = true;
        scheduleTokenRefresh();
        return true;
      }
      return false;
    } catch (error) {
      console.error('Token refresh failed:', error);
      if (error instanceof Error && error.message.includes('401')) {
        // Token is invalid, clear it
        await logout();
      }
      return false;
    }
  }

  function scheduleTokenRefresh() {
    // Clear any existing timeout
    if (refreshTimeout.value) {
      window.clearTimeout(refreshTimeout.value)
    }

    // Schedule refresh 5 minutes before token expiration
    const refreshTime = (525600 - 5) * 60 * 1000 // 1 year minus 5 minutes in milliseconds
    refreshTimeout.value = window.setTimeout(() => {
      refreshToken()
    }, refreshTime)
  }

  async function login(credentials: LoginCredentials) {
    try {
      const params = new URLSearchParams()
      params.append('username', credentials.email)
      params.append('password', credentials.password)

      const data = await apiService.post('/token', params, {
        skipAuth: true
      })
      
      const newToken = data.access_token
      if (newToken) {
        token.value = newToken
        localStorage.setItem('token', newToken)
        isAuthenticated.value = true
        
        // Fetch user data
        await fetchUserData()
        
        // Schedule token refresh
        scheduleTokenRefresh()
        
        return { success: true }
      }
      return { success: false, error: 'Invalid response from server' }
    } catch (error) {
      console.error('Login failed:', error)
      if (error instanceof Error) {
        if (error.message.includes('401')) {
          return { success: false, error: 'Invalid email or password' }
        }
        return { success: false, error: error.message }
      }
      return { success: false, error: 'Login failed. Please try again.' }
    }
  }

  async function loginWithGoogle(code: string, redirectUri: string) {
    try {
      const data = await apiService.post('/auth/google/callback', {
        code,
        redirect_uri: redirectUri
      }, { skipAuth: true })
      
      const newToken = data.access_token
      if (newToken) {
        token.value = newToken
        localStorage.setItem('token', newToken)
        isAuthenticated.value = true
        
        // Fetch user data
        await fetchUserData()
        
        // Schedule token refresh
        scheduleTokenRefresh()
        
        return { success: true, isNewUser: data.user?.is_new_user || false }
      }
      return { success: false, error: 'Invalid response from server' }
    } catch (error) {
      console.error('Google login failed:', error)
      if (error instanceof Error) {
        return { success: false, error: error.message }
      }
      return { success: false, error: 'Google login failed. Please try again.' }
    }
  }

  async function register(data: RegisterData) {
    try {
      const resp = await apiService.post('/users/register', data, { skipAuth: true })
      // Do NOT auto-login anymore. Backend requires email verification first and
      // returns { email_verification_required: true }.
      return { success: true, email_verification_required: !!resp?.email_verification_required }
    } catch (error) {
      console.error('Registration failed:', error)
      if (error instanceof Error) {
        // Try to parse structured error messages
        const errorMessage = error.message
        return { success: false, error: errorMessage }
      }
      return { success: false, error: 'Registration failed' }
    }
  }

  async function logout() {
    token.value = null
    user.value = null
    isAuthenticated.value = false
    
    // Clear all cached data from localStorage
    localStorage.removeItem('token')
    localStorage.removeItem('startDate')
    localStorage.removeItem('endDate')
    
    // Clear all insights cache
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('insights_')) {
        localStorage.removeItem(key)
      }
    })
    
    if (refreshTimeout.value) {
      window.clearTimeout(refreshTimeout.value)
      refreshTimeout.value = null
    }
  }

  async function fetchUserData() {
    try {
      const data = await apiService.get('/users/latest')
      if (data) {
        user.value = data
      } else {
        console.error('No user data received')
        await logout()
      }
    } catch (error) {
      console.error('Failed to fetch user data:', error)
      await logout()
    }
  }

  // Computed property to check if user is admin
  const isAdmin = computed(() => user.value?.is_admin === true)

  return {
    token,
    user,
    isAuthenticated,
    isAdmin,
    login,
    loginWithGoogle,
    register,
    logout,
    fetchUserData,
    refreshToken
  }
}) 